import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import AdminLayout from './Component/AdminLayout';

// Import all page components
import Home from './Component/Pages/Home';
import CustomerReport from './Component/Pages/CustomerReport';
import BusOwnerManage from './Component/Pages/BusOwnerManage';
import BookingManage from './Component/Pages/BookingManage';
import PaymentReport from './Component/Pages/PaymentReport';
import CouponManage from './Component/Pages/CouponManage';
import InfluencerManage from './Component/Pages/InfluencerManage';
import OwnerCommission from './Component/Pages/OwnerCommission';
import Login from './Component/Pages/Login';
import ForgotPassword from './Component/Pages/ForgotPassword';
import InfluencerCommission from './Component/Pages/InfluencerCommission';
import ProfileManagement from './Component/Pages/ProfileManagement';
import RouteManage from './Component/Pages/RouteManage';
import TermsAndConditions from './Component/Pages/TermsAndConditions';
import DriverManagement from './Component/Pages/DriverManagement';
import ConductorManagement from './Component/Pages/ConductorManagement';
import SeatManagement from './Component/Pages/SeatManagement';
import BusFareChart from './Component/Pages/BusFareChart';
import EmployeeManagement from './Component/Pages/EmployeeManagement';
import FreelancerManagement from './Component/Pages/FreelancerManagement';
import ProfitReport from './Component/Pages/ProfitReport';
import AdminExpensesReport from './Component/Pages/AdminExpensesReport';
import GSTAdminPanel from './Component/Pages/GSTAdminPanel';
import MasterRouteManage from './Component/Pages/MasterRouteManage';
import GSTManagement from './Component/Pages/GSTManagement';
import PlatformFeeManagement from './Component/Pages/PlatformFeeManagement';
import BusManagement from './Component/Pages/BusManagement';
import BookingPolicyManagement from './Component/Pages/BookingPolicyManagement';
import BusRouteScheduleManage from './Component/Pages/BusRouteScheduleManage';
import CustomerCareManagement from './Component/Pages/CustomerCareManagement';

// Protected Route wrapper component
const ProtectedRoute = ({ children, allowedRoles, userRole }) => {
  const { token } = useSelector((state) => state.user);
  
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(userRole?.toLowerCase())) {
    return <Navigate to="/unauthorized" replace />;
  }
  
  return children;
};

// Unauthorized Page Component
const UnauthorizedPage = () => {
  return (
    <div style={{
      textAlign: 'center',
      padding: '50px',
      maxWidth: '500px',
      margin: '100px auto'
    }}>
      <div style={{
        fontSize: '48px',
        marginBottom: '20px',
        color: '#dc3545'
      }}>
        ⚠️
      </div>
      <h2 style={{
        color: '#dc3545',
        marginBottom: '20px',
        fontSize: '24px',
        fontWeight: '600'
      }}>
        Unauthorized Access
      </h2>
      <p style={{
        marginBottom: '30px',
        color: '#666',
        fontSize: '16px',
        lineHeight: '1.6'
      }}>
        You don't have permission to access this page.
        Please contact your system administrator if you believe this is a mistake.
      </p>
      <button
        onClick={() => window.location.href = '/dashboard'}
        style={{
          padding: '12px 30px',
          backgroundColor: '#007bff',
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          cursor: 'pointer',
          fontSize: '16px',
          fontWeight: '500'
        }}
      >
        Go to Dashboard
      </button>
    </div>
  );
};

// Route configuration for all pages - NO Router here
const AppRoutes = () => {
  const { token, loginData } = useSelector((state) => state.user);
  const userRole = loginData?.user?.role || loginData?.role || 'admin';
  const isLoggedIn = !!token;

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/unauthorized" element={<UnauthorizedPage />} />

      <Route path="/" element={
        <ProtectedRoute>
          <AdminLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Home />} />
        <Route path="dashboard" element={<Home />} />
 
        <Route path="customers" element={
          <ProtectedRoute allowedRoles={['admin']} userRole={userRole}>
            <CustomerReport />
          </ProtectedRoute>
        } />

        <Route path="GSTManagement" element={
          <ProtectedRoute allowedRoles={['admin']} userRole={userRole}>
            <GSTManagement />
          </ProtectedRoute>
        } />
        
        <Route path="GSTAdminPanel" element={
          <ProtectedRoute allowedRoles={['admin']} userRole={userRole}>
            <GSTAdminPanel />
          </ProtectedRoute>
        } />
        
        {/* Driver Management */}
        <Route path="drivers" element={
          <ProtectedRoute allowedRoles={['admin', 'freelancer']} userRole={userRole}>
            <DriverManagement />
          </ProtectedRoute>
        } />
        
        {/* Conductor Management */}
        <Route path="conductors" element={
          <ProtectedRoute allowedRoles={['admin', 'freelancer']} userRole={userRole}>
            <ConductorManagement />
          </ProtectedRoute>
        } />
        
        {/* Bus Owner Management */}
        <Route path="bus-owners" element={
          <ProtectedRoute allowedRoles={['admin', 'freelancer']} userRole={userRole}>
            <BusOwnerManage />
          </ProtectedRoute>
        } />
        
        {/* Employee Management */}
        <Route path="employees" element={
          <ProtectedRoute allowedRoles={['admin']} userRole={userRole}>
            <EmployeeManagement />
          </ProtectedRoute>
        } />
        
        {/* Freelancer Management */}
        <Route path="freelancers" element={
          <ProtectedRoute allowedRoles={['admin']} userRole={userRole}>
            <FreelancerManagement />
          </ProtectedRoute>
        } />
        
        {/* Route Management */}
        <Route path="routes" element={
          <ProtectedRoute allowedRoles={['admin', 'freelancer']} userRole={userRole}>
            <RouteManage />
          </ProtectedRoute>
        } />
        
        <Route path="masterroutes" element={
          <ProtectedRoute allowedRoles={['admin']} userRole={userRole}>
            <MasterRouteManage />
          </ProtectedRoute>
        } />
        
        {/* Booking Management */}
        <Route path="bookings" element={
          <ProtectedRoute allowedRoles={['admin']} userRole={userRole}>
            <BookingManage />
          </ProtectedRoute>
        } />
        
        {/* Payment Management */}
        <Route path="payments" element={
          <ProtectedRoute allowedRoles={['admin']} userRole={userRole}>
            <PaymentReport />
          </ProtectedRoute>
        } />
        
        {/* Coupon Management */}
        <Route path="coupons" element={
          <ProtectedRoute allowedRoles={['admin']} userRole={userRole}>
            <CouponManage />
          </ProtectedRoute>
        } />
        
        {/* Influencer Management */}
        <Route path="influencers" element={
          <ProtectedRoute allowedRoles={['admin']} userRole={userRole}>
            <InfluencerManage />
          </ProtectedRoute>
        } />
        
        {/* Commission Management */}
        <Route path="owner-commissions" element={
          <ProtectedRoute allowedRoles={['admin']} userRole={userRole}>
            <OwnerCommission />
          </ProtectedRoute>
        } />
        
        <Route path="influencer-commissions" element={
          <ProtectedRoute allowedRoles={['admin']} userRole={userRole}>
            <InfluencerCommission />
          </ProtectedRoute>
        } />
        
        {/* Reports */}
        <Route path="profit-report" element={
          <ProtectedRoute allowedRoles={['admin']} userRole={userRole}>
            <ProfitReport />
          </ProtectedRoute>
        } />
        
        <Route path="expenses-report" element={
          <ProtectedRoute allowedRoles={['admin']} userRole={userRole}>
            <AdminExpensesReport />
          </ProtectedRoute>
        } />
        
        {/* Seat Management */}
        <Route path="seat-management" element={
          <ProtectedRoute allowedRoles={['admin']} userRole={userRole}>
            <SeatManagement />
          </ProtectedRoute>
        } />

<Route path="BusManagement" element={
          <ProtectedRoute allowedRoles={['admin']} userRole={userRole}>
            <BusManagement />
          </ProtectedRoute>
        } />

          <Route path="platform-management" element={
          <ProtectedRoute allowedRoles={['admin']} userRole={userRole}>
            <PlatformFeeManagement />
          </ProtectedRoute>
        } /> 

         {/* Bus Fare Chart */}
        <Route path="customer-care" element={
          <ProtectedRoute allowedRoles={['admin']} userRole={userRole}>
            <CustomerCareManagement />
          </ProtectedRoute>
        } />

        {/* Profile Management */}
        <Route path="profile" element={
          <ProtectedRoute>
            <ProfileManagement />
          </ProtectedRoute>
        } />
        
        {/* Terms and Conditions */}
        <Route path="terms" element={
          <ProtectedRoute allowedRoles={['admin']} userRole={userRole}>
            <TermsAndConditions />
          </ProtectedRoute>
        } />

                {/* Terms and Conditions */}
        <Route path="BookingPolicyManagement" element={
          <ProtectedRoute allowedRoles={['admin']} userRole={userRole}>
            <BookingPolicyManagement />
          </ProtectedRoute>
        } />
        
        {/* Bus Fare Chart */}
        <Route path="chart" element={
          <ProtectedRoute allowedRoles={['admin']} userRole={userRole}>
            <BusFareChart />
          </ProtectedRoute>
        } />

        

        <Route path="busroutes" element={
          <ProtectedRoute allowedRoles={['admin']} userRole={userRole}>
            <BusRouteScheduleManage />
          </ProtectedRoute>
        } />
      </Route>
      
      {/* Catch all route - redirect to dashboard or login */}
      <Route path="*" element={
        isLoggedIn ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />
      } />
    </Routes>
  );
};

export default AppRoutes;